import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import Sidebar from '../components/Sidebar';
import CreditReportViewer from '../components/CreditReportViewer';
import { Plus, RefreshCw, CheckCircle, XCircle, AlertCircle, Save, Upload, Download } from 'lucide-react';

const StatCard = ({ title, value, sub, color = 'primary' }) => {
  const colors = { primary: 'text-primary', emerald: 'text-emerald-500', amber: 'text-amber-500', red: 'text-red-500' };
  return (
    <div className="stat-card animate-fade-in-up" data-accent={color}>
      <p className="text-xs text-muted-foreground font-medium mb-2">{title}</p>
      <p className={`text-3xl font-extrabold ${colors[color] || colors.primary}`}>{value ?? '—'}</p>
      {sub && <p className="text-xs text-muted-foreground mt-2">{sub}</p>}
    </div>
  );
};

const Badge = ({ status }) => {
  if (status === 'active') return <span className="badge-active">Active</span>;
  if (status === 'suspended') return <span className="badge-suspended">Suspended</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-secondary text-muted-foreground">{status}</span>;
};

export default function DeveloperDashboard() {
  const { t, apiCall, user } = useApp();
  const dashLogoRef = useRef(null);
  const landingLogoRef = useRef(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Log tab switch when developer navigates away from KYC & Reports mid-flow
  const handleSetActiveTab = (newTab) => {
    if (activeTab === 'credit-report' && newTab !== 'credit-report') {
      apiCall('POST', '/reports/log-event', {
        event_type: 'tab_switch',
        button_name: '',
        step: 'in-credit-report-flow',
        tab_switched_to: newTab,
        subject_mobile: '',
        local_time: new Date().toLocaleString(),
      }).catch(() => {});
    }
    setActiveTab(newTab);
  };
  const [stats, setStats] = useState({});
  const [admins, setAdmins] = useState([]);
  const [ngos, setNgos] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, ledger: [] });
  const [settings, setSettings] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [emailLogs, setEmailLogs] = useState({ logs: [], total: 0, mock_count: 0, sent_count: 0, failed_count: 0 });
  const [notifications, setNotifications] = useState([]);
  const [logoSettings, setLogoSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [changeMobileFor, setChangeMobileFor] = useState(null);
  const [changeEmailFor,  setChangeEmailFor]  = useState(null);
  const [blockedNums,     setBlockedNums]     = useState([]);
  const [newBlocked, setNewBlocked] = useState({ first_name:'', last_name:'', mobile_no:'', reason:'' });
  const [logFilters, setLogFilters] = useState({ action: '', userType: '', search: '', dateFrom: '', dateTo: '' });
  const [testUsers, setTestUsers] = useState([]);
  const [newTestUser, setNewTestUser] = useState({ name: '', mobile_no: '', email: '' });
  const [naOverview, setNaOverview] = useState({ na_reports: [], claims: [], monthly: {}, kyc_logs: [], kyc_success: 0, kyc_errors: 0 });
  const [naFilters, setNaFilters] = useState({ search: '', status: '', dateFrom: '', dateTo: '' });
  const [kycFilters, setKycFilters] = useState({ search: '', status: '', dateFrom: '', dateTo: '' });
  const [pullAnalytics, setPullAnalytics] = useState({ summary: {}, duplicates: [] });
  const [analyticsFilter, setAnalyticsFilter] = useState({ cause: '', search: '' });
  const [analyticsDays, setAnalyticsDays] = useState(2);
  const [analyticsDateFrom, setAnalyticsDateFrom] = useState('');
  const [analyticsDateTo, setAnalyticsDateTo] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, pub] = await Promise.all([
        apiCall('GET', '/developer/overview'),
        apiCall('GET', '/settings/public')
      ]);
      setStats(s); setLogoSettings(pub);
    } catch {}
    setLoading(false);
  }, [apiCall]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const loaders = {
      admins: () => Promise.all([
        apiCall('GET', '/developer/admins').then(r => setAdmins(r.admins)),
        apiCall('GET', '/developer/test-users').then(r => setTestUsers(r.test_users)),
      ]),
      'na-overview': () => apiCall('GET', '/developer/na-overview').then(setNaOverview),
      'pull-analytics': () => {
        const params = analyticsDateFrom && analyticsDateTo
          ? `start_date=${analyticsDateFrom}&end_date=${analyticsDateTo}`
          : `days=${analyticsDays}`;
        return apiCall('GET', `/developer/duplicate-pull-analytics?${params}`).then(setPullAnalytics);
      },
      ngos: () => apiCall('GET', '/developer/ngos').then(r => setNgos(r.ngos)),
      wallet: () => apiCall('GET', '/developer/wallet').then(setWallet),
      'api-keys': () => apiCall('GET', '/settings/').then(s => setSettings(s)),
      'bundle-settings': () => apiCall('GET', '/settings/').then(s => setSettings(s)),
      'email-templates': () => apiCall('GET', '/settings/').then(s => setSettings(s)),
      logo: () => apiCall('GET', '/settings/').then(s => setSettings(s)),
      'blocked-numbers': () => apiCall('GET', '/developer/blocked-numbers').then(r => setBlockedNums(r.blocked_numbers || [])),
      'audit-logs': () => apiCall('GET', '/developer/audit-logs').then(r => setAuditLogs(r.logs)),
      'email-logs': () => apiCall('GET', '/developer/email-logs').then(setEmailLogs),
      overview: () => Promise.all([
        apiCall('GET', '/developer/overview').then(setStats),
        apiCall('GET', '/developer/notifications').then(r => setNotifications(r.notifications)),
        apiCall('GET', '/settings/public').then(setLogoSettings),
      ]).catch(() => {}),
    };
    if (loaders[activeTab]) loaders[activeTab]().catch(() => {});
  }, [activeTab, apiCall]);

  // Real-time overview polling (30s)
  useEffect(() => {
    if (activeTab !== 'overview') return;
    const timer = setInterval(() => {
      apiCall('GET', '/developer/overview').then(setStats).catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [activeTab, apiCall]);

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This action cannot be undone.`)) return;
    try {
      await apiCall('DELETE', `/developer/users/${userId}`);
      showToast(`User ${userName} deleted`);
      apiCall('GET', '/developer/admins').then(r => setAdmins(r.admins));
      apiCall('GET', '/developer/ngos').then(r => setNgos(r.ngos));
    } catch (err) { showToast(err.response?.data?.detail || 'Error deleting user'); }
  };

  const handleChangeMobile = async () => {
    if (!changeMobileFor?.value?.trim() || changeMobileFor.value.length < 10) {
      showToast('Enter a valid 10-digit mobile number'); return;
    }
    try {
      await apiCall('PATCH', `/developer/users/${changeMobileFor.id}/mobile`, { mobile_no: changeMobileFor.value });
      showToast(`Mobile updated for ${changeMobileFor.name}`);
      setChangeMobileFor(null);
      apiCall('GET', '/developer/admins').then(r => setAdmins(r.admins));
      apiCall('GET', '/developer/ngos').then(r => setNgos(r.ngos));
    } catch (err) { showToast(err.response?.data?.detail || 'Error updating mobile'); }
  };

  const handleChangeEmail = async () => {
    if (!changeEmailFor?.value?.trim() || !changeEmailFor.value.includes('@')) {
      showToast('Enter a valid email address'); return;
    }
    try {
      await apiCall('PATCH', `/developer/users/${changeEmailFor.id}/email`, { email: changeEmailFor.value });
      showToast(`Email updated for ${changeEmailFor.name}`);
      setChangeEmailFor(null);
      apiCall('GET', '/developer/admins').then(r => setAdmins(r.admins));
      apiCall('GET', '/developer/ngos').then(r => setNgos(r.ngos));
    } catch (err) { showToast(err.response?.data?.detail || 'Error updating email'); }
  };

  // ── Tab: Overview ──
  const renderOverview = () => (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
          Welcome back, {user?.name || 'Developer'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Here's your platform overview for today</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title={t('masterBalance')} value={stats.master_wallet_balance} sub="credits" />
        <StatCard title={t('totalAdmins')} value={stats.admin_count} sub={`${stats.active_admins} active`} color="emerald" />
        <StatCard title={t('totalNGOs')} value={stats.ngo_count} sub={`${stats.active_ngos} active`} color="emerald" />
        <StatCard title={t('totalReports')} value={stats.total_reports_pulled} sub={`${stats.total_reports_downloaded} downloaded`} />
        <StatCard title="Emails Sent" value={stats.total_emails_sent} color="amber" />
        <StatCard title={t('adminCredits')} value={stats.total_admin_credits} color="amber" />
        <StatCard title={t('ngoCredits')} value={stats.total_ngo_credits} />
        <StatCard title={t('pendingNotifs')} value={stats.pending_notifications} color={stats.pending_notifications > 0 ? 'red' : 'primary'} />
      </div>
      {notifications.filter(n => !n.acknowledged).length > 0 && (
        <div className="space-y-2 mb-6">
          <h3 className="text-sm font-semibold text-amber-500">Pending Purchase Notifications</h3>
          {notifications.filter(n => !n.acknowledged).map(n => (
            <div key={n.id} className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm">
              <div>
                <p className="font-medium">{n.admin_name} purchased {n.credits} credits (₹{n.amount})</p>
                <p className="text-xs text-muted-foreground">Notifications sent: {n.notification_count} / 5</p>
              </div>
              <button onClick={() => apiCall('POST', `/developer/notifications/${n.id}/acknowledge`).then(load)}
                className="btn-secondary text-xs">Acknowledge</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Tab: Admin Users ──
  const [newAdmin, setNewAdmin] = useState({ name: '', mobile_no: '', email: '' });
  const [assignAdmin, setAssignAdmin] = useState({ admin_id: '', credits: '', action: 'assign' });

  const renderAdmins = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold mb-4 text-sm">{t('createAdmin')}</h3>
        <form className="flex gap-3 flex-wrap" onSubmit={async e => {
          e.preventDefault();
          try { await apiCall('POST', '/developer/admins', newAdmin); showToast('Admin created!'); setNewAdmin({ name: '', mobile_no: '', email: '' }); apiCall('GET', '/developer/admins').then(r => setAdmins(r.admins)); }
          catch (err) { showToast(err.response?.data?.detail || 'Error'); }
        }}>
          <input data-testid="admin-name-input" className="form-input flex-1 min-w-48" placeholder={t('name')} required
            value={newAdmin.name} onChange={e => setNewAdmin(p => ({ ...p, name: e.target.value }))} />
          <input data-testid="admin-mobile-input" className="form-input flex-1 min-w-48" placeholder={t('mobile')} required type="tel"
            value={newAdmin.mobile_no} onChange={e => setNewAdmin(p => ({ ...p, mobile_no: e.target.value }))} />
          <input className="form-input flex-1 min-w-48" placeholder="Email Address" type="email" required
            value={newAdmin.email} onChange={e => setNewAdmin(p => ({ ...p, email: e.target.value }))} />
          <button data-testid="create-admin-btn" type="submit" className="btn-primary flex items-center gap-1.5"><Plus size={14} />{t('create')}</button>
        </form>
      </div>
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold mb-4 text-sm">{t('assignToAdmin')}</h3>
        <form className="flex gap-3 flex-wrap" onSubmit={async e => {
          e.preventDefault();
          const endpoint = assignAdmin.action === 'assign' ? '/developer/wallet/assign-to-admin' : '/developer/wallet/remove-from-admin';
          try { const res = await apiCall('POST', endpoint, { admin_id: assignAdmin.admin_id, credits: parseInt(assignAdmin.credits) });
            showToast(`${assignAdmin.action === 'assign' ? 'Assigned' : 'Removed'} ${assignAdmin.credits} credits`);
            apiCall('GET', '/developer/admins').then(r => setAdmins(r.admins));
            apiCall('GET', '/developer/wallet').then(setWallet); }
          catch (err) { showToast(err.response?.data?.detail || 'Error'); }
        }}>
          <select data-testid="assign-admin-select" className="form-input flex-1 min-w-48"
            value={assignAdmin.admin_id} onChange={e => setAssignAdmin(p => ({ ...p, admin_id: e.target.value }))} required>
            <option value="">Select Admin</option>
            {admins.map(a => <option key={a.id} value={a.id}>{a.name} ({a.credit_balance} credits)</option>)}
          </select>
          <input data-testid="assign-credits-input" className="form-input w-28" type="number" min="1" placeholder="Credits"
            value={assignAdmin.credits} onChange={e => setAssignAdmin(p => ({ ...p, credits: e.target.value }))} required />
          <select className="form-input w-36" value={assignAdmin.action} onChange={e => setAssignAdmin(p => ({ ...p, action: e.target.value }))}>
            <option value="assign">Assign</option>
            <option value="remove">Remove</option>
          </select>
          <button type="submit" className="btn-primary">{t('save')}</button>
        </form>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm"><thead><tr className="border-b border-border bg-secondary/30">
          {[t('userName'), t('userMobile'), t('userBalance'), t('userReports'), t('userStatus'), t('actions')].map(h => (
            <th key={h} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">{h}</th>
          ))}
        </tr></thead><tbody>
          {admins.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">{t('noUsers')}</td></tr> :
            admins.map(a => (
              <tr key={a.id} className="border-b border-border/50 hover:bg-secondary/20">
                <td className="px-4 py-3 font-medium">{a.name}</td>
                <td className="px-4 py-3 font-mono-code text-xs">{a.mobile_no}</td>
                <td className="px-4 py-3 font-bold text-primary">{a.credit_balance}</td>
                <td className="px-4 py-3">{a.reports_pulled}</td>
                <td className="px-4 py-3"><Badge status={a.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    <button onClick={async () => {
                      const newStatus = a.status === 'active' ? 'suspended' : 'active';
                      await apiCall('PATCH', `/developer/users/${a.id}/status`, { status: newStatus });
                      apiCall('GET', '/developer/admins').then(r => setAdmins(r.admins));
                    }} className={`text-xs px-2 py-1 rounded border ${a.status === 'active' ? 'text-destructive border-destructive/30 hover:bg-destructive/5' : 'text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/5'}`}>
                      {a.status === 'active' ? t('suspend') : t('activate')}
                    </button>
                    <button onClick={() => setChangeMobileFor({ id: a.id, name: a.name, current: a.mobile_no, value: '' })}
                      className="text-xs px-2 py-1 rounded border text-primary border-primary/30 hover:bg-primary/5">
                      Edit Mobile
                    </button>
                    <button onClick={() => setChangeEmailFor({ id: a.id, name: a.name, value: '' })}
                      className="text-xs px-2 py-1 rounded border text-blue-500 border-blue-500/30 hover:bg-blue-500/5">
                      Edit Email
                    </button>
                    <button onClick={() => handleDeleteUser(a.id, a.name)}
                      className="text-xs px-2 py-1 rounded border text-destructive border-destructive/30 hover:bg-destructive/5">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
        </tbody></table>
      </div>

      {/* ── Test Users ──────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-sm">Test Admin Users</h3>
          <span className="px-2 py-0.5 bg-amber-500/10 text-amber-600 text-xs rounded-full font-medium">OTP on screen</span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Test users bypass email OTP — the code appears directly on the login screen. Share mobile &amp; email with testers.
        </p>
        <form className="flex gap-3 flex-wrap mb-4" onSubmit={async e => {
          e.preventDefault();
          try {
            await apiCall('POST', '/developer/test-users', newTestUser);
            showToast('Test user created!');
            setNewTestUser({ name: '', mobile_no: '', email: '' });
            apiCall('GET', '/developer/test-users').then(r => setTestUsers(r.test_users));
          } catch (err) { showToast(err.response?.data?.detail || 'Error'); }
        }}>
          <input className="form-input flex-1 min-w-40" placeholder="Display Name" required
            value={newTestUser.name} onChange={e => setNewTestUser(p => ({ ...p, name: e.target.value }))} />
          <input className="form-input flex-1 min-w-40" placeholder="Mobile Number" type="tel" required
            value={newTestUser.mobile_no} onChange={e => setNewTestUser(p => ({ ...p, mobile_no: e.target.value }))} />
          <input className="form-input flex-1 min-w-40" placeholder="Email (optional)" type="email"
            value={newTestUser.email} onChange={e => setNewTestUser(p => ({ ...p, email: e.target.value }))} />
          <button type="submit" className="btn-primary flex items-center gap-1.5"><Plus size={14} />Create Test User</button>
        </form>
        {testUsers.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
              {['Name', 'Mobile (Login)', 'Email', 'OTP Mode', 'Balance', 'Action'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium">{h}</th>
              ))}
            </tr></thead><tbody>
              {testUsers.map(u => (
                <tr key={u.id} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="px-3 py-2 font-medium">{u.name}</td>
                  <td className="px-3 py-2 font-mono-code">{u.mobile_no}</td>
                  <td className="px-3 py-2 text-muted-foreground">{u.email || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-600 rounded text-xs font-medium">OTP on screen</span>
                  </td>
                  <td className="px-3 py-2 font-bold text-primary">{u.credit_balance}</td>
                  <td className="px-3 py-2">
                    <button onClick={async () => {
                      if (!window.confirm(`Delete test user "${u.name}"?`)) return;
                      await apiCall('DELETE', `/developer/test-users/${u.id}`);
                      showToast('Test user deleted');
                      apiCall('GET', '/developer/test-users').then(r => setTestUsers(r.test_users));
                    }} className="text-xs px-2 py-1 rounded border text-destructive border-destructive/30 hover:bg-destructive/5">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}
      </div>
    </div>
  );
  const renderNGOs = () => (
    <div>
      <h2 className="text-xl font-bold mb-6">{t('ngoUsers')}</h2>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm"><thead><tr className="border-b border-border bg-secondary/30">
          {[t('userName'), t('userMobile'), t('userBalance'), t('userReports'), t('userStatus'), 'Created By', t('actions')].map(h => (
            <th key={h} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">{h}</th>
          ))}
        </tr></thead><tbody>
          {ngos.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-muted-foreground text-sm">{t('noUsers')}</td></tr> :
            ngos.map(n => (
              <tr key={n.id} className="border-b border-border/50 hover:bg-secondary/20">
                <td className="px-4 py-3 font-medium">{n.name}</td>
                <td className="px-4 py-3 font-mono-code text-xs">{n.mobile_no}</td>
                <td className="px-4 py-3 font-bold text-primary">{n.credit_balance}</td>
                <td className="px-4 py-3">{n.reports_pulled}</td>
                <td className="px-4 py-3"><Badge status={n.status} /></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{n.created_by_name || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    <button onClick={async () => {
                      const newStatus = n.status === 'active' ? 'suspended' : 'active';
                      await apiCall('PATCH', `/developer/users/${n.id}/status`, { status: newStatus });
                      apiCall('GET', '/developer/ngos').then(r => setNgos(r.ngos));
                    }} className={`text-xs px-2 py-1 rounded border ${n.status === 'active' ? 'text-destructive border-destructive/30 hover:bg-destructive/5' : 'text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/5'}`}>
                      {n.status === 'active' ? t('suspend') : t('activate')}
                    </button>
                    <button onClick={() => setChangeMobileFor({ id: n.id, name: n.name, current: n.mobile_no, value: '' })}
                      className="text-xs px-2 py-1 rounded border text-primary border-primary/30 hover:bg-primary/5">
                      Edit Mobile
                    </button>
                    <button onClick={() => setChangeEmailFor({ id: n.id, name: n.name, value: '' })}
                      className="text-xs px-2 py-1 rounded border text-blue-500 border-blue-500/30 hover:bg-blue-500/5">
                      Edit Email
                    </button>
                    <button onClick={() => handleDeleteUser(n.id, n.name)}
                      className="text-xs px-2 py-1 rounded border text-destructive border-destructive/30 hover:bg-destructive/5">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );

  // ── Tab: Master Wallet ──
  const [addCreds, setAddCreds] = useState({ credits: '', reason: '' });
  const renderWallet = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title="Current Balance" value={wallet.balance} sub="credits available" />
        <StatCard title="Total Added" value={wallet.total_added} color="emerald" />
        <StatCard title="Total Assigned" value={wallet.total_assigned} color="amber" />
      </div>
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold mb-4 text-sm">{t('addCredits')}</h3>
        <form className="flex gap-3" onSubmit={async e => {
          e.preventDefault();
          try { const res = await apiCall('POST', '/developer/wallet/add', { credits: parseInt(addCreds.credits), reason: addCreds.reason });
            showToast(`Added ${addCreds.credits} credits. New balance: ${res.new_balance}`);
            setAddCreds({ credits: '', reason: '' }); apiCall('GET', '/developer/wallet').then(setWallet); }
          catch (err) { showToast(err.response?.data?.detail || 'Error'); }
        }}>
          <input data-testid="add-credits-input" className="form-input w-32" type="number" min="1" placeholder="Credits" required
            value={addCreds.credits} onChange={e => setAddCreds(p => ({ ...p, credits: e.target.value }))} />
          <input className="form-input flex-1" placeholder="Reason (optional)"
            value={addCreds.reason} onChange={e => setAddCreds(p => ({ ...p, reason: e.target.value }))} />
          <button data-testid="add-credits-submit" type="submit" className="btn-primary">{t('addCredits')}</button>
        </form>
      </div>
      <div>
        <h3 className="font-semibold mb-3 text-sm">Recent Transactions</h3>
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
            {['Type', t('description'), t('amount'), 'Balance After', t('date')].map(h => (
              <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium">{h}</th>
            ))}
          </tr></thead><tbody>
            {wallet.ledger?.map((e, i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${e.transaction_type === 'credit' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'}`}>
                    {e.transaction_type === 'credit' ? '+' : '-'}{e.amount}
                  </span>
                </td>
                <td className="px-3 py-2 max-w-xs truncate">{e.description}</td>
                <td className="px-3 py-2 font-medium">{e.amount}</td>
                <td className="px-3 py-2 font-bold">{e.balance_after}</td>
                <td className="px-3 py-2 text-muted-foreground">{new Date(e.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      </div>
    </div>
  );

  // ── Tab: API Keys ──
  const [testResult, setTestResult] = useState({});
  const saveSettings = async (updates) => {
    try {
      await apiCall('PUT', '/settings/', updates);
      showToast('Settings saved!');
      // refresh both settings and logoSettings so sidebar reflects changes instantly
      await Promise.all([
        apiCall('GET', '/settings/').then(setSettings),
        apiCall('GET', '/settings/public').then(setLogoSettings),
      ]);
    } catch (err) { showToast(err.response?.data?.detail || 'Error saving'); }
  };
  const renderAPIKeys = () => (
    <div className="space-y-6">
      {/* IDS Pay */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm">IDS Pay (Credit Report API)</h3>
          <button onClick={() => apiCall('POST', '/developer/api-keys/test-ids').then(r => setTestResult(p => ({ ...p, ids: r })))}
            className="btn-secondary text-xs">{t('testConnection')}</button>
        </div>
        {testResult.ids && (
          <div className={`mb-3 px-3 py-2 rounded text-xs ${testResult.ids.success ? 'bg-emerald-500/10 text-emerald-600' : 'bg-destructive/10 text-destructive'}`}>
            {testResult.ids.success ? '✓ ' + testResult.ids.message : '✗ ' + testResult.ids.message}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {[
            ['ids_pay_api_id', 'API ID'],
            ['ids_pay_api_key', 'API Key'],
            ['ids_pay_token_id', 'Token ID'],
            ['ids_pay_base_url', 'Base URL'],
            ['aadhaar_endpoint', 'Aadhaar Endpoint (e.g. /uidai/aadhaar-details)'],
          ].map(([key, label]) => (
            <div key={key} className={key === 'aadhaar_endpoint' ? 'col-span-2' : ''}>
              <label className="form-label">{label}</label>
              <input className="form-input font-mono-code text-xs" placeholder={key === 'ids_pay_base_url' ? 'https://javabackend.idspay.in/api/v1/prod' : label}
                defaultValue={settings[key] || ''} onBlur={e => saveSettings({ [key]: e.target.value })} />
            </div>
          ))}
        </div>
      </div>
      {/* AWS SES */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm">AWS SES (Email Service)</h3>
          <button onClick={() => apiCall('POST', '/developer/api-keys/test-ses').then(r => setTestResult(p => ({ ...p, ses: r })))}
            className="btn-secondary text-xs">{t('testConnection')}</button>
        </div>
        {testResult.ses && (
          <div className={`mb-3 px-3 py-2 rounded text-xs ${testResult.ses.success ? 'bg-emerald-500/10 text-emerald-600' : 'bg-destructive/10 text-destructive'}`}>
            {testResult.ses.success ? '✓ ' + testResult.ses.message : '✗ ' + testResult.ses.message}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {[
            ['ses_access_key_id', 'Access Key ID'],
            ['ses_secret_access_key', 'Secret Access Key'],
            ['ses_region', 'AWS Region (e.g. ap-south-1)'],
            ['ses_from_email', 'From Email (verified in SES)'],
            ['ses_from_name', 'From Name'],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="form-label">{label}</label>
              <input className="form-input font-mono-code text-xs"
                type={key.includes('secret') ? 'password' : 'text'}
                placeholder={key === 'ses_region' ? 'ap-south-1' : label}
                defaultValue={settings[key] || ''} onBlur={e => saveSettings({ [key]: e.target.value })} />
            </div>
          ))}
        </div>
      </div>
      {/* Razorpay */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-sm mb-4">Razorpay (Payments)</h3>
        <div className="grid grid-cols-2 gap-3">
          {[['razorpay_key_id', 'Key ID'], ['razorpay_key_secret', 'Key Secret']].map(([key, label]) => (
            <div key={key}>
              <label className="form-label">{label}</label>
              <input className="form-input font-mono-code text-xs" type={key.includes('secret') ? 'password' : 'text'} placeholder={label}
                defaultValue={settings[key] || ''} onBlur={e => saveSettings({ [key]: e.target.value })} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Tab: Bundle Settings ──
  const renderBundleSettings = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-sm mb-4">Credit Cost</h3>
        <div className="flex items-center gap-4">
          <div>
            <label className="form-label">Cost per Credit (₹)</label>
            <input className="form-input w-32" type="number" step="0.01"
              defaultValue={settings.credit_cost_per_unit || 26}
              onBlur={e => saveSettings({ credit_cost_per_unit: parseFloat(e.target.value) })} />
          </div>
        </div>
      </div>
      {[1, 2].map(b => (
        <div key={b} className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-sm mb-4">{t(`bundle${b}`)} Configuration</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Credits</label>
              <input className="form-input" type="number" min="1"
                defaultValue={settings[`bundle_${b}_credits`] || (b === 1 ? 350 : 700)}
                onBlur={e => saveSettings({ [`bundle_${b}_credits`]: parseInt(e.target.value) })} />
            </div>
            <div>
              <label className="form-label">Price (₹)</label>
              <input className="form-input" type="number" step="0.01"
                defaultValue={settings[`bundle_${b}_price`] || (b === 1 ? 9100 : 18200)}
                onBlur={e => saveSettings({ [`bundle_${b}_price`]: parseFloat(e.target.value) })} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // ── Tab: Email Templates ──
  const [smsForm, setSmsForm] = useState({});
  useEffect(() => { if (settings) setSmsForm(settings); }, [settings]);
  const renderEmailTemplates = () => (
    <div className="space-y-4">
      <div className="px-4 py-3 bg-primary/5 border border-primary/15 rounded-xl text-xs text-muted-foreground">
        Template variables: <code className="bg-secondary px-1 rounded">{'{login_url}'}</code> login link ·
        <code className="bg-secondary px-1 rounded mx-1">{'{name}'}</code> user name ·
        <code className="bg-secondary px-1 rounded">{'{otp}'}</code> OTP code ·
        <code className="bg-secondary px-1 rounded mx-1">{'{admin_name}'}</code> ·
        <code className="bg-secondary px-1 rounded">{'{credits}'}</code> ·
        <code className="bg-secondary px-1 rounded mx-1">{'{amount}'}</code>
      </div>
      {[
        ['email_signature', 'Email Signature (appended to all emails)'],
        ['otp_email_template', 'OTP / Authentication Email Template'],
        ['developer_notification_email_template', 'Developer — Credit Purchase Notification Email'],
        ['admin_activation_email_template', 'Admin Account Activation Email'],
        ['ngo_activation_email_template', 'NGO User Activation Email'],
        ['checkbox_consent_content', t('checkboxContent')],
        ['pdf_footer_note', 'PDF Footer Note (Credit Report)'],
      ].map(([key, label]) => (
        <div key={key} className="bg-card border border-border rounded-xl p-4">
          <label className="form-label">{label}</label>
          <textarea className="form-input min-h-[80px] resize-none" rows={key === 'email_signature' ? 2 : 3}
            value={smsForm[key] || ''}
            onChange={e => setSmsForm(p => ({ ...p, [key]: e.target.value }))} />
        </div>
      ))}
      <div>
        <label className="form-label">App URL (used in email links)</label>
        <input className="form-input" value={smsForm.app_url || ''}
          onChange={e => setSmsForm(p => ({ ...p, app_url: e.target.value }))} />
      </div>
      <button onClick={() => saveSettings(smsForm)} className="btn-primary">{t('save')}</button>
    </div>
  );

  // ── Tab: Logo Settings ──
  const handleLogoFile = (settingKey, ref) => {
    const file = ref.current?.files[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(file.type)) { showToast('Please select a PNG or JPG file'); return; }
    if (file.size > 2 * 1024 * 1024) { showToast('File must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = ev => {
      saveSettings({ [settingKey]: ev.target.result });
      // clear the input so same file can be re-selected
      if (ref.current) ref.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const LogoBlock = ({ label, settingKey, fileRef }) => (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="font-semibold text-sm mb-4">{label}</h3>
      <div className="space-y-4">
        {/* file upload */}
        <div>
          <label className="form-label">Upload PNG / JPG</label>
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,image/png,image/jpeg"
              onChange={() => handleLogoFile(settingKey, fileRef)} className="hidden" />
            <button type="button" onClick={() => fileRef.current?.click()}
              className="btn-secondary flex items-center gap-2 text-sm">
              <Upload size={14} /> Choose File
            </button>
            <span className="text-xs text-muted-foreground">PNG or JPG · max 2 MB</span>
          </div>
        </div>
        {/* URL fallback */}
        <div>
          <label className="form-label">Or paste an image URL</label>
          <input className="form-input font-mono-code text-xs"
            key={settings[settingKey]?.startsWith('data:') ? 'b64' : 'url'}
            defaultValue={settings[settingKey]?.startsWith('data:') ? '' : (settings[settingKey] || '')}
            onBlur={e => { if (e.target.value.trim()) saveSettings({ [settingKey]: e.target.value.trim() }); }}
            placeholder="https://example.com/logo.png" />
        </div>
        {/* preview */}
        {settings[settingKey] && (
          <div className="flex items-center gap-4">
            <div className="p-3 bg-secondary/40 rounded-xl border border-border">
              <img src={settings[settingKey]} alt="preview"
                className="h-12 w-auto object-contain max-w-[200px]" />
            </div>
            <button type="button" onClick={() => saveSettings({ [settingKey]: '' })}
              className="text-xs text-destructive hover:text-destructive/80 transition-colors">
              Remove logo
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderLogo = () => (
    <div className="space-y-6">
      {/* App name text */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-sm mb-1">App Name / Text</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Displayed next to the logo in the sidebar and on the login page.
        </p>
        <input className="form-input" placeholder="e.g. MyCreditPlatform"
          defaultValue={settings.app_name || ''}
          onBlur={e => saveSettings({ app_name: e.target.value })} />
      </div>

      {/* Text colour & size */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-sm mb-1">Text Colour &amp; Size</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Controls the colour and size of the app name text shown next to the logo.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Dashboard colour */}
          <div>
            <label className="form-label">Dashboard / Sidebar Colour</label>
            <div className="flex items-center gap-2 mb-1">
              <input type="color"
                key={`dc-${settings.logo_text_color}`}
                defaultValue={settings.logo_text_color || '#1e293b'}
                onBlur={e => saveSettings({ logo_text_color: e.target.value })}
                className="w-9 h-9 rounded-lg border border-border cursor-pointer p-0.5 bg-transparent flex-shrink-0" />
              <input className="form-input flex-1 font-mono-code text-xs"
                key={`dc2-${settings.logo_text_color}`}
                defaultValue={settings.logo_text_color || ''}
                onBlur={e => saveSettings({ logo_text_color: e.target.value })}
                placeholder="empty = auto (theme)" />
            </div>
            <p className="text-xs text-muted-foreground">Leave empty for auto dark/light.</p>
          </div>
          {/* Landing page colour */}
          <div>
            <label className="form-label">Landing Page Text Colour</label>
            <div className="flex items-center gap-2 mb-1">
              <input type="color"
                key={`lc-${settings.logo_text_color_landing}`}
                defaultValue={settings.logo_text_color_landing || '#ffffff'}
                onBlur={e => saveSettings({ logo_text_color_landing: e.target.value })}
                className="w-9 h-9 rounded-lg border border-border cursor-pointer p-0.5 bg-transparent flex-shrink-0" />
              <input className="form-input flex-1 font-mono-code text-xs"
                key={`lc2-${settings.logo_text_color_landing}`}
                defaultValue={settings.logo_text_color_landing || '#ffffff'}
                onBlur={e => saveSettings({ logo_text_color_landing: e.target.value })}
                placeholder="#ffffff" />
            </div>
            <p className="text-xs text-muted-foreground">Defaults to white (image background).</p>
          </div>
        </div>
        {/* Size */}
        <div className="mt-5">
          <label className="form-label">Text Size</label>
          <div className="flex gap-2 flex-wrap">
            {[
              { k: 'xs',   l: 'XS',  d: '12 px' },
              { k: 'sm',   l: 'SM',  d: '14 px' },
              { k: 'base', l: 'MD',  d: '16 px' },
              { k: 'lg',   l: 'LG',  d: '18 px' },
              { k: 'xl',   l: 'XL',  d: '20 px' },
            ].map(s => (
              <button key={s.k} type="button"
                onClick={() => saveSettings({ logo_text_size: s.k })}
                className={`px-3 py-2 rounded-lg border text-xs font-medium transition-all flex flex-col items-center
                  ${(settings.logo_text_size || 'sm') === s.k
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:bg-secondary text-foreground'}`}>
                <span className="font-bold">{s.l}</span>
                <span className="text-muted-foreground" style={{ fontSize: 10 }}>{s.d}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <LogoBlock label="Dashboard Logo"     settingKey="logo_url"         fileRef={dashLogoRef} />
      <LogoBlock label="Landing Page Logo"  settingKey="landing_logo_url" fileRef={landingLogoRef} />

      {/* Size */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-sm mb-4">{t('logoSize')}</h3>
        <div className="flex gap-3">
          {['small', 'medium', 'large'].map(size => (
            <button key={size} type="button" onClick={() => saveSettings({ logo_size: size })}
              className={`px-4 py-2 rounded-lg border text-sm font-medium capitalize transition-all
                ${settings.logo_size === size
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:bg-secondary'}`}>
              {t(size)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Tab: Blocked Numbers ──
  const renderBlockedNumbers = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-sm mb-1">Block Name + Mobile Combination</h3>
        <p className="text-xs text-muted-foreground mb-4">
          When a user tries to pull a credit report with a blocked name + mobile, they will see an "Access Denied" error.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="form-label">First Name</label>
            <input className="form-input" placeholder="First name" value={newBlocked.first_name}
              onChange={e => setNewBlocked(p => ({ ...p, first_name: e.target.value }))} />
          </div>
          <div>
            <label className="form-label">Last Name</label>
            <input className="form-input" placeholder="Last name" value={newBlocked.last_name}
              onChange={e => setNewBlocked(p => ({ ...p, last_name: e.target.value }))} />
          </div>
          <div>
            <label className="form-label">Mobile Number</label>
            <input className="form-input" placeholder="10-digit mobile" type="tel" value={newBlocked.mobile_no}
              onChange={e => setNewBlocked(p => ({ ...p, mobile_no: e.target.value.replace(/\D/g,'') }))} maxLength={10} />
          </div>
          <div>
            <label className="form-label">Reason (optional)</label>
            <input className="form-input" placeholder="Reason" value={newBlocked.reason}
              onChange={e => setNewBlocked(p => ({ ...p, reason: e.target.value }))} />
          </div>
        </div>
        <button onClick={async () => {
          if (!newBlocked.first_name || !newBlocked.last_name || !newBlocked.mobile_no) {
            showToast('All fields except reason are required'); return;
          }
          try {
            await apiCall('POST', '/developer/blocked-numbers', newBlocked);
            showToast('Number blocked successfully');
            setNewBlocked({ first_name:'', last_name:'', mobile_no:'', reason:'' });
            apiCall('GET', '/developer/blocked-numbers').then(r => setBlockedNums(r.blocked_numbers || []));
          } catch (err) { showToast(err.response?.data?.detail || 'Error'); }
        }} className="btn-destructive flex items-center gap-2">
          Block This Entry
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-secondary/30">
            {['First Name','Last Name','Mobile','Reason','Blocked On','Action'].map(h => (
              <th key={h} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {blockedNums.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">No blocked entries</td></tr>
            ) : blockedNums.map(b => (
              <tr key={b.id} className="border-b border-border/50 hover:bg-secondary/20">
                <td className="px-4 py-3 font-medium">{b.first_name}</td>
                <td className="px-4 py-3 font-medium">{b.last_name}</td>
                <td className="px-4 py-3 font-mono-code text-xs">{b.mobile_no}</td>
                <td className="px-4 py-3 text-muted-foreground">{b.reason || '—'}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <button onClick={async () => {
                    if (!window.confirm(`Unblock ${b.first_name} ${b.last_name}?`)) return;
                    try {
                      await apiCall('DELETE', `/developer/blocked-numbers/${b.id}`);
                      showToast('Entry unblocked');
                      setBlockedNums(prev => prev.filter(x => x.id !== b.id));
                    } catch (err) { showToast(err.response?.data?.detail || 'Error'); }
                  }} className="text-xs px-2 py-1 rounded border text-destructive border-destructive/30 hover:bg-destructive/5">
                    Unblock
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ── Tab: Audit Logs ──
  const filteredLogs = auditLogs.filter(log => {
    if (logFilters.action   && log.action    !== logFilters.action)   return false;
    if (logFilters.userType && log.user_type !== logFilters.userType) return false;
    if (logFilters.search) {
      const q = logFilters.search.toLowerCase();
      if (!(log.details   || '').toLowerCase().includes(q) &&
          !(log.action    || '').toLowerCase().includes(q) &&
          !(log.user_name || '').toLowerCase().includes(q)) return false;
    }
    if (logFilters.dateFrom && new Date(log.created_at) < new Date(logFilters.dateFrom)) return false;
    if (logFilters.dateTo   && new Date(log.created_at) > new Date(logFilters.dateTo + 'T23:59:59')) return false;
    return true;
  });

  const uniqueActions   = [...new Set(auditLogs.map(l => l.action).filter(Boolean))].sort();
  const uniqueUserTypes = [...new Set(auditLogs.map(l => l.user_type).filter(Boolean))].sort();

  const downloadLogs = (format) => {
    const cols = ['Timestamp','Action','User','Credit Score','Details','IP Address'];
    const rows = filteredLogs.map(log => {
      const userLabel = log.user_type === 'ngo'   ? `NGO User: ${log.user_name || 'NGO'}`
                      : log.user_type === 'admin' ? `Admin: ${log.user_name || 'Admin'}`
                      : 'Developer';
      const score = log.action === 'pull_credit_report'
        ? ('credit_score' in log ? (log.credit_score ?? 'N/A') : 'Blank')
        : '-';
      return [new Date(log.created_at).toLocaleString(), (log.action||'').replace(/_/g,' '),
              userLabel, score, log.details||'', log.ip_address||''];
    });
    const filename = `audit_logs_${new Date().toISOString().split('T')[0]}`;
    if (format === 'csv') {
      const csv = [cols,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
      const a   = Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([csv],{type:'text/csv;charset=utf-8;'})),download:`${filename}.csv`});
      a.click();
    } else {
      const tbl = `<table><tr>${cols.map(c=>`<th>${c}</th>`).join('')}</tr>${rows.map(r=>`<tr>${r.map(c=>`<td>${c}</td>`).join('')}</tr>`).join('')}</table>`;
      const a   = Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([`<html><head><meta charset="utf-8"></head><body>${tbl}</body></html>`],{type:'application/vnd.ms-excel'})),download:`${filename}.xls`});
      a.click();
    }
  };

  const renderAuditLogs = () => (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-semibold text-sm">{t('auditLogs')}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => apiCall('GET', '/developer/audit-logs').then(r => setAuditLogs(r.logs))}
            className="btn-secondary text-xs flex items-center gap-1.5"><RefreshCw size={12} />{t('refresh')}</button>
          <button onClick={() => downloadLogs('csv')}
            className="btn-secondary text-xs flex items-center gap-1.5"><Download size={12} />CSV</button>
          <button onClick={() => downloadLogs('excel')}
            className="btn-secondary text-xs flex items-center gap-1.5"><Download size={12} />Excel</button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3 p-3 bg-secondary/30 rounded-xl border border-border">
        <select className="form-input text-xs py-1.5" value={logFilters.action}
          onChange={e => setLogFilters(p => ({ ...p, action: e.target.value }))}>
          <option value="">All Actions</option>
          {uniqueActions.map(a => <option key={a} value={a}>{a.replace(/_/g,' ')}</option>)}
        </select>
        <select className="form-input text-xs py-1.5" value={logFilters.userType}
          onChange={e => setLogFilters(p => ({ ...p, userType: e.target.value }))}>
          <option value="">All Users</option>
          {uniqueUserTypes.map(u => <option key={u} value={u} className="capitalize">{u}</option>)}
        </select>
        <input className="form-input text-xs py-1.5" placeholder="Search details / user…"
          value={logFilters.search} onChange={e => setLogFilters(p => ({ ...p, search: e.target.value }))} />
        <input type="date" className="form-input text-xs py-1.5"
          value={logFilters.dateFrom} onChange={e => setLogFilters(p => ({ ...p, dateFrom: e.target.value }))} />
        <div className="flex items-center gap-1">
          <input type="date" className="form-input text-xs py-1.5 flex-1"
            value={logFilters.dateTo} onChange={e => setLogFilters(p => ({ ...p, dateTo: e.target.value }))} />
          {(logFilters.action||logFilters.userType||logFilters.search||logFilters.dateFrom||logFilters.dateTo) && (
            <button onClick={() => setLogFilters({action:'',userType:'',search:'',dateFrom:'',dateTo:''})}
              className="text-xs px-2 py-1.5 rounded border border-destructive/30 text-destructive hover:bg-destructive/5 whitespace-nowrap">
              Clear
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-2">Showing {filteredLogs.length} of {auditLogs.length} entries</p>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
          {[t('action'), t('details'), 'User', 'Credit Score', t('ipAddress'), t('timestamp')].map(h => (
            <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
          ))}
        </tr></thead><tbody>
          {filteredLogs.length === 0
            ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">{t('noLogs')}</td></tr>
            : filteredLogs.map(log => (
              <tr key={log.id} className="border-b border-border/50 hover:bg-secondary/20">
                <td className="px-3 py-2 font-medium capitalize whitespace-nowrap">{log.action?.replace(/_/g,' ')}</td>
                <td className="px-3 py-2 max-w-xs truncate text-muted-foreground">{log.details}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {log.user_type === 'ngo'   ? <span>NGO User: <strong>{log.user_name||'NGO'}</strong></span>
                   : log.user_type === 'admin' ? <span>Admin: <strong>{log.user_name||'Admin'}</strong></span>
                   : <span className="text-primary font-medium">Developer</span>}
                </td>
                <td className="px-3 py-2 text-center font-bold">
                  {log.action === 'pull_credit_report'
                    ? ('credit_score' in log
                        ? (log.credit_score
                            ? <span className="text-emerald-600">{log.credit_score}</span>
                            : <span className="text-muted-foreground text-xs">N/A</span>)
                        : <span className="text-muted-foreground text-xs italic">Blank</span>)
                    : <span className="text-muted-foreground/40">—</span>}
                </td>
                <td className="px-3 py-2 font-mono-code">{log.ip_address}</td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );

  // ── Tab: SMS Logs ──
  const renderEmailLogs = () => (
    <div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Emails" value={emailLogs.total} />
        <StatCard title="Sent" value={emailLogs.sent_count} color="emerald" />
        <StatCard title="Mock (No SES)" value={emailLogs.mock_count} color="amber" />
        <StatCard title="Failed" value={emailLogs.failed_count} color="red" />
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
          {['To', 'Subject', t('purpose'), t('status'), t('timestamp')].map(h => (
            <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium">{h}</th>
          ))}
        </tr></thead><tbody>
          {emailLogs.logs?.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">{t('noLogs')}</td></tr> :
          emailLogs.logs?.map(log => (
            <tr key={log.id} className="border-b border-border/50 hover:bg-secondary/20">
              <td className="px-3 py-2 font-mono-code text-xs max-w-[160px] truncate">{log.recipient}</td>
              <td className="px-3 py-2 max-w-[200px] truncate">{log.subject}</td>
              <td className="px-3 py-2 capitalize">{log.purpose?.replace(/_/g, ' ')}</td>
              <td className="px-3 py-2">
                <span className={`px-1.5 py-0.5 rounded text-xs ${log.status === 'sent' ? 'bg-emerald-500/10 text-emerald-600' : log.status === 'mock' ? 'bg-amber-500/10 text-amber-600' : 'bg-destructive/10 text-destructive'}`}>
                  {log.status}
                </span>
              </td>
              <td className="px-3 py-2 text-muted-foreground">{new Date(log.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );

  const renderNaOverview = () => {
    const { na_reports, claims, kyc_logs, kyc_success, kyc_errors } = naOverview;

    const filteredNa = na_reports.filter(r => {
      if (naFilters.status && r.created_by_role !== naFilters.status) return false;
      if (naFilters.search) {
        const q = naFilters.search.toLowerCase();
        if (!(r.subject_name||'').toLowerCase().includes(q) && !(r.user_name||'').toLowerCase().includes(q) && !(r.subject_mobile||'').toLowerCase().includes(q)) return false;
      }
      if (naFilters.dateFrom && new Date(r.created_at) < new Date(naFilters.dateFrom)) return false;
      if (naFilters.dateTo   && new Date(r.created_at) > new Date(naFilters.dateTo + 'T23:59:59')) return false;
      return true;
    });

    const filteredKyc = kyc_logs.filter(k => {
      if (kycFilters.status && k.status !== kycFilters.status) return false;
      if (kycFilters.search) {
        const q = kycFilters.search.toLowerCase();
        if (!(k.user_name||'').toLowerCase().includes(q) && !(k.mobile_no||'').toLowerCase().includes(q) && !(k.error_message||'').toLowerCase().includes(q)) return false;
      }
      if (kycFilters.dateFrom && new Date(k.created_at) < new Date(kycFilters.dateFrom)) return false;
      if (kycFilters.dateTo   && new Date(k.created_at) > new Date(kycFilters.dateTo + 'T23:59:59')) return false;
      return true;
    });

    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Total N/A Reports" value={na_reports.length} color="amber" />
          <StatCard title="Total Claims" value={claims.length} color="primary" />
          <StatCard title="KYC Success" value={kyc_success} color="emerald" />
          <StatCard title="KYC Errors" value={kyc_errors} color="red" />
        </div>

        {/* Claims history */}
        {claims.length > 0 && (
          <div>
            <h4 className="font-semibold text-sm mb-2">All Claims</h4>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
                {['Admin', 'Email', 'Credits', 'N/A Count', 'Month', 'Claimed At', 'Status'].map(h=>(
                  <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium">{h}</th>
                ))}</tr></thead><tbody>
                {claims.map(c=>(
                  <tr key={c.id} className="border-b border-border/50 hover:bg-secondary/20">
                    <td className="px-3 py-2 font-medium">{c.admin_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{c.admin_email||'—'}</td>
                    <td className="px-3 py-2 font-bold text-emerald-600">{c.credits_claimed}</td>
                    <td className="px-3 py-2">{c.monthly_na_count||'—'}</td>
                    <td className="px-3 py-2">{c.month}/{c.year}</td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{new Date(c.claimed_at).toLocaleString()}</td>
                    <td className="px-3 py-2"><span className="px-1.5 py-0.5 bg-amber-500/10 text-amber-600 rounded text-xs">{c.status}</span></td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>
        )}

        {/* N/A Reports with filters */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-sm">N/A Reports ({filteredNa.length}/{na_reports.length})</h4>
            <button onClick={() => apiCall('GET', '/developer/na-overview').then(setNaOverview)}
              className="btn-secondary text-xs flex items-center gap-1"><RefreshCw size={11} />Refresh</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 p-3 bg-secondary/30 rounded-xl border border-border">
            <select className="form-input text-xs py-1.5" value={naFilters.status}
              onChange={e=>setNaFilters(p=>({...p,status:e.target.value}))}>
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="ngo">NGO</option>
            </select>
            <input className="form-input text-xs py-1.5" placeholder="Search name / mobile…"
              value={naFilters.search} onChange={e=>setNaFilters(p=>({...p,search:e.target.value}))} />
            <input type="date" className="form-input text-xs py-1.5"
              value={naFilters.dateFrom} onChange={e=>setNaFilters(p=>({...p,dateFrom:e.target.value}))} />
            <input type="date" className="form-input text-xs py-1.5"
              value={naFilters.dateTo} onChange={e=>setNaFilters(p=>({...p,dateTo:e.target.value}))} />
          </div>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
              {['Role','User','Subject','Mobile','Credits Used','Timestamp'].map(h=>(
                <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}</tr></thead><tbody>
              {filteredNa.length===0 ? <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">No N/A reports</td></tr>
                : filteredNa.map(r=>(
                <tr key={r.id} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="px-3 py-2 capitalize">{r.created_by_role}</td>
                  <td className="px-3 py-2 font-medium">{r.user_name||'—'}</td>
                  <td className="px-3 py-2">{r.subject_name}</td>
                  <td className="px-3 py-2 font-mono-code">{r.subject_mobile}</td>
                  <td className="px-3 py-2 text-center"><span className="text-red-600 font-semibold">{r.credits_deducted??1}</span></td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </div>

        {/* Aadhaar KYC Logs with filters */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-sm">Aadhaar KYC Fetch Logs ({filteredKyc.length}/{kyc_logs.length})</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 p-3 bg-secondary/30 rounded-xl border border-border">
            <select className="form-input text-xs py-1.5" value={kycFilters.status}
              onChange={e=>setKycFilters(p=>({...p,status:e.target.value}))}>
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
            </select>
            <input className="form-input text-xs py-1.5" placeholder="Search user / mobile / error…"
              value={kycFilters.search} onChange={e=>setKycFilters(p=>({...p,search:e.target.value}))} />
            <input type="date" className="form-input text-xs py-1.5"
              value={kycFilters.dateFrom} onChange={e=>setKycFilters(p=>({...p,dateFrom:e.target.value}))} />
            <input type="date" className="form-input text-xs py-1.5"
              value={kycFilters.dateTo} onChange={e=>setKycFilters(p=>({...p,dateTo:e.target.value}))} />
          </div>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
              {['Status','User','Role','Aadhaar','Mobile','Credits','Error','Timestamp'].map(h=>(
                <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}</tr></thead><tbody>
              {filteredKyc.length===0 ? <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">No KYC logs yet</td></tr>
                : filteredKyc.map((k,i)=>(
                <tr key={k.id||i} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${k.status==='success'?'bg-emerald-500/10 text-emerald-600':'bg-red-500/10 text-red-600'}`}>
                      {k.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium">{k.user_name||'—'}</td>
                  <td className="px-3 py-2 capitalize">{k.user_type}</td>
                  <td className="px-3 py-2 font-mono-code">XXXX XXXX {k.aadhaar_last4}</td>
                  <td className="px-3 py-2 font-mono-code">{k.mobile_no||'—'}</td>
                  <td className="px-3 py-2 text-center">{k.credits_deducted}</td>
                  <td className="px-3 py-2 max-w-xs truncate text-red-600">{k.error_message||'—'}</td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{new Date(k.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </div>
      </div>
    );
  };

  // ── Pull Analytics ──────────────────────────────────────────
  const renderPullAnalytics = () => {
    const { summary, duplicates } = pullAnalytics;
    const CAUSE = {
      RAPID_CLICK: { text: 'Rapid Click',  cls: 'bg-red-500/10 text-red-600',     icon: '🖱' },
      TAB_SWITCH:  { text: 'Tab Switch',   cls: 'bg-amber-500/10 text-amber-600',  icon: '⇄' },
      INTENTIONAL: { text: 'Intentional', cls: 'bg-emerald-500/10 text-emerald-600', icon: '' },
    };

    const loadData = () => {
      const params = analyticsDateFrom && analyticsDateTo
        ? `start_date=${analyticsDateFrom}&end_date=${analyticsDateTo}`
        : `days=2`;
      apiCall('GET', `/developer/duplicate-pull-analytics?${params}`).then(setPullAnalytics);
    };

    // Filtered flat incident rows (bug events only)
    const flatRows = [];
    duplicates.forEach(d => {
      d.incidents.filter(i => i.cause !== 'INTENTIONAL').forEach(inc => {
        if (analyticsFilter.cause && inc.cause !== analyticsFilter.cause) return;
        if (analyticsFilter.search) {
          const q = analyticsFilter.search.toLowerCase();
          if (!d.subject_name?.toLowerCase().includes(q) &&
              !d.subject_mobile?.includes(q) &&
              !inc.puller_name?.toLowerCase().includes(q) &&
              !inc.puller_mobile?.includes(q)) return;
        }
        flatRows.push({ ...inc, subject_name: d.subject_name, subject_mobile: d.subject_mobile, reason_summary: d.reason_summary });
      });
    });
    flatRows.sort((a, b) => b.pulled_at?.localeCompare(a.pulled_at));

    const downloadCSV = () => {
      const cols = ['Date & Time','Event Type','Reason','Admin/NGO Name','Admin Mobile','Report Subject Name','Subject Mobile','IP Address','Gap (seconds)','Credits Charged','Credits Wasted'];
      const rows = flatRows.map(i => [
        new Date(i.pulled_at).toLocaleString(),
        i.cause,
        i.reason_summary || i.cause,
        i.puller_name, i.puller_mobile,
        i.subject_name, i.subject_mobile,
        i.ip_address || '',
        i.gap_seconds,
        i.credits_charged, i.credits_wasted
      ]);
      const period = summary.start_date && summary.end_date ? `${summary.start_date}_to_${summary.end_date}` : 'range';
      const csv = [cols,...rows].map(r=>r.map(v=>`"${String(v??'').replace(/"/g,'""')}"`).join(',')).join('\n');
      const a = Object.assign(document.createElement('a'),{
        href: URL.createObjectURL(new Blob([csv],{type:'text/csv;charset=utf-8;'})),
        download: `pull_analytics_${period}.csv`
      });
      a.click();
    };

    const dateLabel = summary.start_date
      ? `${summary.start_date} → ${summary.end_date}`
      : 'Select date range and click Load';

    return (
      <div className="space-y-5">
        {/* ── Controls ── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-semibold text-sm">Pull Analytics</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{dateLabel}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadCSV} disabled={flatRows.length === 0}
              className="btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-40">
              <Download size={12} />Download CSV
            </button>
          </div>
        </div>

        {/* ── Custom Date Range ── */}
        <div className="p-4 bg-secondary/30 border border-border rounded-xl">
          <p className="text-xs font-semibold text-foreground mb-3">Custom Date Range</p>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="form-label text-xs">From Date</label>
              <input type="date" className="form-input text-xs py-1.5"
                value={analyticsDateFrom} onChange={e => setAnalyticsDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="form-label text-xs">To Date</label>
              <input type="date" className="form-input text-xs py-1.5"
                value={analyticsDateTo} onChange={e => setAnalyticsDateTo(e.target.value)} />
            </div>
            <button onClick={loadData}
              disabled={!analyticsDateFrom || !analyticsDateTo}
              className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-40 self-end">
              <RefreshCw size={12} />Load Report
            </button>
            {(analyticsDateFrom || analyticsDateTo) && (
              <button onClick={() => { setAnalyticsDateFrom(''); setAnalyticsDateTo(''); setPullAnalytics({summary:{},duplicates:[]}); }}
                className="btn-secondary text-xs self-end">Clear</button>
            )}
          </div>
        </div>

        {/* ── Summary Stats ── */}
        {Object.keys(summary).length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Subjects Affected', val: summary.total_mobiles_affected ?? 0, sub: 'unique users', color: 'text-destructive' },
              { label: 'Rapid Click Events', val: summary.total_rapid_click_incidents ?? 0, sub: 'gap <5s (button clicked multiple times)', color: 'text-destructive' },
              { label: 'Tab Switch Events', val: summary.total_tab_switch_incidents ?? 0, sub: 'gap 5–60s (tab switched mid-flow)', color: 'text-amber-600' },
              { label: 'Credits Wasted', val: summary.total_wasted_credits ?? 0, sub: 'needs refund to admin', color: 'text-destructive' },
            ].map(c => (
              <div key={c.label} className="bg-card border border-border rounded-xl p-4">
                <p className="text-xs text-muted-foreground font-medium">{c.label}</p>
                <p className={`text-3xl font-extrabold ${c.color} my-1`}>{c.val}</p>
                <p className="text-xs text-muted-foreground">{c.sub}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Filter row ── */}
        {flatRows.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <select className="form-input text-xs py-1.5 w-48" value={analyticsFilter.cause}
              onChange={e => setAnalyticsFilter(p => ({...p, cause: e.target.value}))}>
              <option value="">All Event Types</option>
              <option value="RAPID_CLICK">Rapid Click only</option>
              <option value="TAB_SWITCH">Tab Switch only</option>
            </select>
            <input className="form-input text-xs py-1.5 flex-1 min-w-48"
              placeholder="Search admin name, admin mobile, subject name or mobile…"
              value={analyticsFilter.search}
              onChange={e => setAnalyticsFilter(p => ({...p, search: e.target.value}))} />
            <span className="text-xs text-muted-foreground">{flatRows.length} event{flatRows.length !== 1 ? 's' : ''}</span>
          </div>
        )}

        {/* ── Main Table ── */}
        {Object.keys(summary).length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
            Select a date range above and click <strong>Load Report</strong>
          </div>
        ) : flatRows.length === 0 ? (
          <div className="text-center py-12 text-emerald-600 text-sm border border-border rounded-xl bg-emerald-500/5">
            No Rapid Click or Tab Switch events in this period — all reports were pulled exactly once.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  {['Date & Time','Event Type','Reason','Admin / NGO Name','Admin Mobile','Report Subject','Subject Mobile','IP Address','Gap','Credits Charged','Wasted'].map(h => (
                    <th key={h} className="text-left px-3 py-3 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {flatRows.map((inc, i) => {
                  const cl = CAUSE[inc.cause] || CAUSE.INTENTIONAL;
                  return (
                    <tr key={i} className="border-b border-border/40 hover:bg-secondary/10">
                      <td className="px-3 py-2.5 whitespace-nowrap font-medium">
                        {new Date(inc.pulled_at).toLocaleDateString()}<br/>
                        <span className="text-muted-foreground text-[10px]">{new Date(inc.pulled_at).toLocaleTimeString()}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded font-semibold whitespace-nowrap ${cl.cls}`}>{cl.text}</span>
                      </td>
                      <td className="px-3 py-2.5 max-w-[200px]">
                        <span className="text-muted-foreground leading-tight block">{inc.reason_summary || inc.cause}</span>
                        {/* Actual tracked events (new data) */}
                        {inc.click_events?.length > 0 && inc.click_events.map((ev, ci) => (
                          <div key={ci} className="mt-1 text-[10px] text-red-600 font-medium">
                            Button: <strong>"{ev.button_name || 'Get Credit Report'}"</strong> clicked at {ev.local_time || new Date(ev.created_at).toLocaleTimeString()}
                          </div>
                        ))}
                        {inc.switch_events?.length > 0 && inc.switch_events.map((ev, si) => (
                          <div key={si} className="mt-1 text-[10px] text-amber-600 font-medium">
                            Tab switched to: <strong>"{ev.tab_to || 'unknown'}"</strong> at {ev.local_time || new Date(ev.created_at).toLocaleTimeString()}
                            {ev.step && ev.step !== 'in-credit-report-flow' && <span className="ml-1 text-muted-foreground">(was on step: {ev.step})</span>}
                          </div>
                        ))}
                        {/* Historical inferred data (no events yet recorded) */}
                        {inc.click_events?.length === 0 && inc.cause === 'RAPID_CLICK' && (
                          <div className="mt-1 text-[10px] text-red-500">
                            Button: <strong>"Get Credit Report"</strong> — approx {new Date(inc.pulled_at).toLocaleTimeString()} (inferred)
                          </div>
                        )}
                        {inc.switch_events?.length === 0 && inc.cause === 'TAB_SWITCH' && (
                          <div className="mt-1 text-[10px] text-amber-500">
                            Tab switched — approx {new Date(new Date(inc.pulled_at).getTime() - (inc.gap_seconds - 5) * 1000).toLocaleTimeString()} (inferred, exact tab unknown for historical data)
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-primary whitespace-nowrap">
                        {inc.puller_name || <span className="text-muted-foreground">{inc.puller_role}</span>}
                      </td>
                      <td className="px-3 py-2.5 font-mono-code whitespace-nowrap">{inc.puller_mobile || '—'}</td>
                      <td className="px-3 py-2.5 font-medium whitespace-nowrap">{inc.subject_name}</td>
                      <td className="px-3 py-2.5 font-mono-code whitespace-nowrap">{inc.subject_mobile}</td>
                      <td className="px-3 py-2.5 font-mono-code text-[10px]">{inc.ip_address || '—'}</td>
                      <td className="px-3 py-2.5">
                        <span className="font-semibold text-amber-600 whitespace-nowrap">{inc.gap_seconds}s</span>
                      </td>
                      <td className="px-3 py-2.5 text-center font-bold">{inc.credits_charged}</td>
                      <td className="px-3 py-2.5 text-center font-bold text-destructive">−{inc.credits_wasted}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-secondary/30 font-semibold">
                  <td colSpan={9} className="px-3 py-2.5 text-right text-xs">
                    {flatRows.length} event{flatRows.length !== 1 ? 's' : ''} · Total Credits Wasted:
                  </td>
                  <td colSpan={2} className="px-3 py-2.5 font-bold text-destructive text-sm text-center">
                    −{flatRows.reduce((s, i) => s + (i.credits_wasted || 0), 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    );
  };

  const TABS = {
    'overview': renderOverview,
    'admins': renderAdmins,
    'ngos': renderNGOs,
    'wallet': renderWallet,
    'api-keys': renderAPIKeys,
    'bundle-settings': renderBundleSettings,
    'email-templates': renderEmailTemplates,
    'logo': renderLogo,
    'blocked-numbers': renderBlockedNumbers,
    'credit-report': () => <CreditReportViewer consentText={settings.checkbox_consent_content} pdfFooterNote={settings.pdf_footer_note} />,
    'na-overview': renderNaOverview,
    'pull-analytics': renderPullAnalytics,
    'audit-logs': renderAuditLogs,
    'email-logs': renderEmailLogs,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="developer-dashboard">
      <Sidebar role="developer" activeTab={activeTab} setActiveTab={handleSetActiveTab} logoSettings={logoSettings} />
      <main className="lg:pl-64 pt-14 lg:pt-4 lg:pr-4 lg:pb-4">
        <div className="bg-white dark:bg-card lg:rounded-2xl shadow-sm dark:shadow-none min-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-100 dark:border-border bg-white dark:bg-card shrink-0">
            <span className="text-sm font-semibold text-foreground">
              {activeTab.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                {user?.name?.[0]?.toUpperCase() || 'D'}
              </div>
              <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || 'Developer'}</span>
            </div>
          </div>
          <div className="flex-1 p-6 lg:p-8">
            {(TABS[activeTab] || (() => <div>Coming soon</div>))()}
          </div>
        </div>
      </main>
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 px-4 py-3 bg-white dark:bg-card border border-gray-100 dark:border-border rounded-xl shadow-lg text-sm animate-fade-in-up">
          {toast}
        </div>
      )}
      {/* Change Mobile Modal */}
      {changeMobileFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setChangeMobileFor(null)}>
          <div className="bg-white dark:bg-card rounded-2xl p-6 w-96 shadow-2xl border border-border" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-1">Change Mobile Number</h3>
            <p className="text-sm text-muted-foreground mb-1">User: <strong>{changeMobileFor.name}</strong></p>
            <p className="text-sm text-muted-foreground mb-4">
              Current: <code className="bg-secondary px-1.5 py-0.5 rounded text-xs font-mono">{changeMobileFor.current}</code>
            </p>
            <input className="form-input mb-4" type="tel" placeholder="New 10-digit mobile number"
              value={changeMobileFor.value}
              onChange={e => setChangeMobileFor(p => ({ ...p, value: e.target.value.replace(/\D/g, '') }))}
              maxLength={10} />
            <div className="flex gap-2">
              <button onClick={handleChangeMobile} className="btn-primary flex-1">Save Changes</button>
              <button onClick={() => setChangeMobileFor(null)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* Change Email Modal */}
      {changeEmailFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setChangeEmailFor(null)}>
          <div className="bg-white dark:bg-card rounded-2xl p-6 w-96 shadow-2xl border border-border" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-1">Change Email Address</h3>
            <p className="text-sm text-muted-foreground mb-4">User: <strong>{changeEmailFor.name}</strong></p>
            <input className="form-input mb-4" type="email" placeholder="New email address"
              value={changeEmailFor.value}
              onChange={e => setChangeEmailFor(p => ({ ...p, value: e.target.value }))} />
            <div className="flex gap-2">
              <button onClick={handleChangeEmail} className="btn-primary flex-1">Save Changes</button>
              <button onClick={() => setChangeEmailFor(null)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
